import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, DataTable, Page, PageContentHeader, PageEmptyState, Searchbox, SortDirection, useFilterAndSortData } from 'seagull';
import { CreateProposalButton } from '~/components/CreateProposalButton/CreateProposalButton';
import PageLoadingState from '~/components/PageLoadingState';
import { useIoContext } from '~/contexts';
import { useSetDocumentTitle } from '~/hooks/useSetDocumentTitle';
import { useStateFromUrl } from '~/hooks/useStateFromUrl';
import { useTableColumnMenu } from '~/hooks/useTableColumnMenu';
import { useTablePagination } from '~/hooks/useTablePagination';
import { useLocaleKeys } from '~/i18n';
import { timeModel } from '~/models';
import { timeTextsPath } from '~/models/time/selectors';
import { ArchivedProposalsColumnIds, DEFAULT_ARCHIVED_PROPOSALS_SORT, archivedProposalsColumns, archivedProposalsSortColumnDefs, toArchivedProposalRows, toArchivedProposalsColumnId } from './ArchivedProposalsDashboard.table.utils';
import { ArchivedProposalsDashboardTestIds } from './ArchivedProposalsDashboard.types';
import ArchivedProposalsEmptyState from './assets/ArchivedProposalsEmptyState.svg';
import { useArchivedProposalActions } from './hooks/useArchivedProposalActions';
import { useArchivedProposals } from './hooks/useArchivedProposals';
import { useUnarchiveDraft } from './hooks/useUnarchiveDraft';
export const ArchivedProposalsDashboard = ()=>{
    const { t: archivedProposalsDashboardTexts } = useLocaleKeys('proposal.archivedDashboard');
    const { t: commonTexts } = useLocaleKeys('common');
    const { t: timeTexts } = useLocaleKeys(timeTextsPath);
    const { getNow } = useIoContext();
    const navigate = useNavigate();
    const columnDefs = useMemo(()=>archivedProposalsSortColumnDefs(archivedProposalsDashboardTexts.columns), []);
    const [mainColumn, ...columns] = useMemo(()=>archivedProposalsColumns(archivedProposalsDashboardTexts), []);
    const { drafts: rawDrafts, isLoading, isBackgroundLoading } = useArchivedProposals({
        refreshOnHookMount: true
    });
    const [tableSort, setTableSort] = useState(DEFAULT_ARCHIVED_PROPOSALS_SORT);
    const { urlState: { search }, changeUrlState } = useStateFromUrl({
        search: {
            defaultValue: ''
        }
    });
    const filter = useMemo(()=>({
            [ArchivedProposalsColumnIds.CompanyName]: {
                search
            }
        }), [
        search
    ]);
    const sort = useMemo(()=>({
            [toArchivedProposalsColumnId(tableSort.name)]: {
                direction: tableSort.direction === 'asc' ? SortDirection.Asc : SortDirection.Desc
            }
        }), [
        tableSort
    ]);
    const { data: drafts } = useFilterAndSortData({
        data: rawDrafts,
        columnDefs,
        filter,
        sort
    });
    const unarchivingDraftId = useRef('');
    const { unarchiveDraft } = useUnarchiveDraft({
        onSuccess: ()=>navigate("/draft/".concat(unarchivingDraftId.current)),
        biCode: 18802
    });
    const buildRowActions = useArchivedProposalActions();
    const columnMenu = useTableColumnMenu('archived_proposals');
    const archivedRows = useMemo(()=>toArchivedProposalRows({
            drafts: drafts !== null && drafts !== void 0 ? drafts : [],
            formatArchivedDate: (draft)=>timeModel.formatTimeAgo(draft.lastModifiedOn, timeTexts, getNow().getTime()),
            unnamedDraftLabel: archivedProposalsDashboardTexts.actions.unnamedDraft()
        }), [
        drafts
    ]);
    const { pagedItems: pagedRows, pagination } = useTablePagination(archivedRows);
    useSetDocumentTitle('Archived proposals');
    if (isLoading) return /*#__PURE__*/ _jsx(PageLoadingState, {});
    const hasDrafts = rawDrafts != null && rawDrafts.length > 0;
    const onClearSearch = ()=>changeUrlState({
            search: ''
        });
    return /*#__PURE__*/ _jsxs(Page, {
        testId: ArchivedProposalsDashboardTestIds.Root(),
        variant: "containedHeight",
        header: archivedProposalsDashboardTexts.header(),
        actions: /*#__PURE__*/ _jsx(CreateProposalButton, {}),
        loading: isBackgroundLoading,
        children: [
            !hasDrafts && /*#__PURE__*/ _jsx(PageEmptyState, {
                testId: ArchivedProposalsDashboardTestIds.EmptyState.Root(),
                illustration: /*#__PURE__*/ _jsx(ArchivedProposalsEmptyState, {}),
                title: archivedProposalsDashboardTexts.emptyState.title(),
                message: archivedProposalsDashboardTexts.emptyState.message(),
                actions: /*#__PURE__*/ _jsx(CreateProposalButton, {
                    testId: ArchivedProposalsDashboardTestIds.EmptyState.CreateProposalButton(),
                    text: archivedProposalsDashboardTexts.emptyState.createProposal()
                })
            }),
            hasDrafts && /*#__PURE__*/ _jsx(PageContentHeader, {
                children: /*#__PURE__*/ _jsx(Searchbox, {
                    bi: {
                        name: 'archived_proposals_search'
                    },
                    placeholder: commonTexts.searchPlaceholder(),
                    registerHotkey: true,
                    testId: ArchivedProposalsDashboardTestIds.Search(),
                    value: search,
                    onChange: (event)=>changeUrlState({
                            search: event.target.value
                        }),
                    onClear: onClearSearch
                })
            }),
            hasDrafts && drafts != null && drafts.length === 0 && /*#__PURE__*/ _jsx(PageEmptyState, {
                illustration: /*#__PURE__*/ _jsx(ArchivedProposalsEmptyState, {}),
                title: archivedProposalsDashboardTexts.noMatchingFilters.title(),
                message: archivedProposalsDashboardTexts.noMatchingFilters.message(),
                testId: ArchivedProposalsDashboardTestIds.EmptySearch.Root(),
                actions: /*#__PURE__*/ _jsx(Button, {
                    bi: {
                        name: 'clear_archived_proposals_search'
                    },
                    testId: ArchivedProposalsDashboardTestIds.EmptySearch.ClearSearch(),
                    onClick: onClearSearch,
                    children: archivedProposalsDashboardTexts.clearSearch()
                })
            }),
            hasDrafts && drafts != null && drafts.length > 0 && /*#__PURE__*/ _jsx(DataTable, {
                mainColumn: mainColumn,
                allowColumnResize: true,
                allowColumnReorder: true,
                reorderBi: {
                    name: 'archived_proposals_reorder_columns'
                },
                allowColumnHide: true,
                columnMenu: columnMenu,
                columns: columns,
                items: pagedRows,
                pagination: pagination,
                getRowId: (row)=>row.id,
                mobile: {
                    fields: [
                        mainColumn,
                        ...columns
                    ].map((column)=>({
                            name: column.name,
                            label: column.header
                        }))
                },
                sort: {
                    name: tableSort.name,
                    direction: tableSort.direction,
                    sortedBy: 'consumer',
                    onChange: setTableSort
                },
                rowActions: {
                    menuBi: {
                        name: 'archived_proposal_actions'
                    },
                    menu: (row)=>buildRowActions({
                            draftId: row.id,
                            unarchiveDraft
                        })
                },
                testId: ArchivedProposalsDashboardTestIds.Table()
            })
        ]
    });
};
